
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useUpdateClientPaymentStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ clientId, paymentStatus }: { clientId: string; paymentStatus: string }) => {
      console.log('Updating client payment status:', { clientId, paymentStatus });

      const { data, error } = await supabase.rpc(
        'update_client_payment_status',
        {
          p_client_id: clientId,
          p_payment_status: paymentStatus
        }
      );

      if (error) {
        console.error('Error updating payment status:', error);
        throw new Error(`Failed to update payment status: ${error.message}`);
      }

      console.log('Payment status updated successfully:', data);
      return data;
    },
    onSuccess: async (_, variables) => {
      console.log('Payment status mutation succeeded, updating UI state...');
      
      // Invalidate all client-related queries
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['clients'] }),
        queryClient.invalidateQueries({ queryKey: ['client', variables.clientId] }),
      ]);
      
      // Force immediate refetch of critical data
      await Promise.all([
        queryClient.refetchQueries({ 
          queryKey: ['client', variables.clientId],
          type: 'active'
        }),
        queryClient.refetchQueries({ 
          queryKey: ['clients'],
          type: 'active'
        })
      ]);

      // Update client data in cache immediately
      queryClient.setQueryData(['client', variables.clientId], (oldData: any) => {
        if (oldData) {
          return {
            ...oldData,
            paymentStatus: variables.paymentStatus
          };
        }
        return oldData;
      });

      // Update clients list cache
      queryClient.setQueryData(['clients'], (oldData: any[]) => {
        if (oldData) {
          return oldData.map(client => 
            client.id === variables.clientId 
              ? { ...client, paymentStatus: variables.paymentStatus }
              : client
          );
        }
        return oldData;
      });

      toast.success('Payment status updated successfully');
      console.log('UI state updated successfully with new payment status');
    },
    onError: (error) => {
      console.error('Payment status mutation failed:', error);
      toast.error(`Failed to update payment status: ${error.message}`);
    }
  });
};
